/**
 * Consent Management (RGPD / LOPDGDD / AEPD compliant)
 *
 * Stores user consent per category in localStorage.
 * PostHog (and any other analytics/marketing SDK) is loaded ONLY
 * after the user explicitly consents — never before, never by default.
 *
 * Categories:
 *  - essential : always on (site functionality, language preference)
 *  - analytics : PostHog (session replay, pageviews, funnel analytics)
 *  - marketing : future use (pixel, retargeting) — off by default
 *
 * Version of the consent schema: bumping this value re-prompts every user.
 */

const STORAGE_KEY = 'remalex_consent_v2';
const CURRENT_VERSION = 2;

export const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
export const POSTHOG_HOST = 'https://us.i.posthog.com';

export const DEFAULT_CONSENT = {
  essential: true,  // cannot be disabled
  analytics: false,
  marketing: false,
};

/**
 * Read the stored consent. Returns null if not yet consented (show banner).
 */
export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CURRENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persist consent and notify listeners.
 */
export function setConsent(partial) {
  const record = {
    ...DEFAULT_CONSENT,
    ...partial,
    essential: true,           // always true, cannot be opted out
    version: CURRENT_VERSION,
    timestamp: Date.now(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent('consent-changed', { detail: record }));
  return record;
}

/**
 * Wipe the stored consent — useful when user clicks "Manage cookies" and
 * we want to re-prompt from scratch.
 */
export function resetConsent() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('consent-changed', { detail: null }));
}

/**
 * Subscribe to consent changes.
 */
export function onConsentChange(callback) {
  const handler = (e) => callback(e.detail);
  window.addEventListener('consent-changed', handler);
  return () => window.removeEventListener('consent-changed', handler);
}

/* ─────────────────────────────────────────────────────────────
   PostHog — dynamic loader, triggered only after consent
─────────────────────────────────────────────────────────────── */

let posthogLoaded = false;

/**
 * Load PostHog if analytics consent has been given.
 * Idempotent — safe to call multiple times.
 */
export function loadPostHogIfConsented() {
  const consent = getConsent();
  if (!consent || !consent.analytics) return;
  if (posthogLoaded || typeof window === 'undefined') return;
  posthogLoaded = true;

  // Inject PostHog snippet dynamically (not in index.html anymore)
  const script = document.createElement('script');
  script.async = true;
  script.src = POSTHOG_HOST.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
  script.onload = () => {
    if (!window.posthog) return;
    window.posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      session_recording: {
        recordCrossOriginIframes: false,
        capturePerformance: false,
      },
      // Respect Do-Not-Track header
      respect_dnt: true,
    });
  };
  document.head.appendChild(script);

  // Create stub so early calls queue properly before the script loads
  if (!window.posthog) {
    window.posthog = [];
    window.posthog.push = function () {
      window.posthog.q = window.posthog.q || [];
      window.posthog.q.push(arguments);
    };
  }
}

/**
 * If consent is withdrawn, opt out from PostHog capture.
 * We don't unload the script (not possible), but we stop all tracking.
 */
export function revokePostHogIfNeeded() {
  const consent = getConsent();
  if (consent?.analytics) return;
  if (typeof window !== 'undefined' && window.posthog?.opt_out_capturing) {
    try { window.posthog.opt_out_capturing(); } catch {}
  }
}

/* ─────────────────────────────────────────────────────────────
   Google Analytics 4 — Consent Mode v2
─────────────────────────────────────────────────────────────── */

const GA4_ID = 'G-D3LPRMM5N4';

function gtag(...args) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

export function grantGA4Consent() {
  gtag('consent', 'update', { analytics_storage: 'granted' });
  gtag('event', 'page_view');
}

export function revokeGA4Consent() {
  gtag('consent', 'update', { analytics_storage: 'denied' });
}

export function trackEvent(eventName, params = {}) {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', eventName, { ...params, send_to: GA4_ID });
}

/**
 * Initialize consent-based loading. Call once on app startup.
 */
export function initConsent() {
  const consent = getConsent();
  if (consent?.analytics) grantGA4Consent();

  loadPostHogIfConsented();
  onConsentChange((c) => {
    if (c?.analytics) {
      grantGA4Consent();
      loadPostHogIfConsented();
    } else {
      revokeGA4Consent();
      revokePostHogIfNeeded();
    }
  });
}
